import React from "react";
import { Box, Stack, Typography, alpha } from "@mui/material";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import { costMethodOptions } from "./sharedBasedPaymentData";

const Metric = ({ label, value, secondary = false }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      component='dt'
      variant='caption'
      color='text.secondary'
      sx={{ mb: 0.5 }}
    >
      {label}
    </Typography>
    <Typography
      component='dd'
      sx={{
        m: 0,
        fontSize: secondary ? "0.875rem" : "1.125rem",
        fontWeight: secondary ? 500 : 600,
        lineHeight: 1.5,
        fontVariantNumeric: "tabular-nums",
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const gridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
  columnGap: 3,
  rowGap: 1.5,
  m: 0,
};

const TrackerMiniMetrics = ({
  results = [],
  currency,
  closingOnly = false,
  sx = {},
}) => {
  if (!results.length) return null;
  const money = (value) =>
    `${currency ? `${currency} ` : ""}${formatAmount(value, false, false, false, true)}`;

  return (
    <Stack
      spacing={2}
      sx={{
        mt: 2.5,
        pt: 2,
        borderTop: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
        ...sx,
      }}
    >
      {results.map(({ pool, summary }) => (
        <Box
          key={pool.poolId}
          component='section'
          aria-label={`${pool.poolName || pool.poolId} tracker summary`}
        >
          {results.length > 1 && (
            <Typography variant='body2' fontWeight={600} sx={{ mb: 1.5 }}>
              {pool.poolName || pool.poolId}
            </Typography>
          )}
          <Box
            component='dl'
            sx={{
              ...gridSx,
              ...(closingOnly
                ? {
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  }
                : {}),
            }}
          >
            <Metric
              label='Closing Shares'
              value={summary.issue ? "—" : formatAmount(summary.closingShares)}
            />
            <Metric
              label='Closing cost'
              value={summary.issue ? "—" : money(summary.closingCost)}
            />
            {!closingOnly && (
              <Metric
                label='Method'
                value={
                  costMethodOptions.find(
                    (option) => option.value === pool.method
                  )?.label || "FIFO"
                }
                secondary
              />
            )}
          </Box>
          {!closingOnly && pool.method === "annualWeightedAverage" && (
            <Box
              component='dl'
              sx={{
                ...gridSx,
                mt: 2,
                px: 1.5,
                py: 1.25,
                borderRadius: 1.5,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
              }}
            >
              <Metric
                label='Current YA shares'
                value={
                  summary.issue ? "—" : formatAmount(summary.currentYaShares)
                }
                secondary
              />
              <Metric
                label='Current YA cost'
                value={summary.issue ? "—" : money(summary.currentYaCost)}
                secondary
              />
              <Metric
                label='YA avg cost/share'
                value={
                  summary.issue ? "—" : money(summary.annualAverageCostPerShare)
                }
                secondary
              />
            </Box>
          )}
          {summary.issue && (
            <Typography
              variant='caption'
              color='warning.main'
              sx={{ display: "block", mt: 1.5 }}
            >
              {summary.issue}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
};

export default TrackerMiniMetrics;
